import React, { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Bot } from "lucide-react";
import { Player } from "@lottiefiles/react-lottie-player";

interface SmartIconProps {
  iconUrl?: string | null;
  fallbackIcon?: React.ReactNode;
  className?: string;
  size?: number;
  alt?: string;
}

export const SmartIcon: React.FC<SmartIconProps> = ({
  iconUrl,
  fallbackIcon = <Bot className="h-6 w-6 text-white" />,
  className = "",
  size = 40,
  alt = "Icon",
}) => {
  const [imageError, setImageError] = useState(false);
  const [lottieError, setLottieError] = useState(false);

  // Check if the iconUrl is a Lottie file (ends with .json or .lottie)
  const isLottieUrl =
    iconUrl &&
    (iconUrl.endsWith(".json") ||
      iconUrl.endsWith(".lottie") ||
      iconUrl.includes("lottie"));

  if (!iconUrl || imageError) {
    return (
      <Avatar className={className} style={{ width: size, height: size }}>
        <AvatarFallback className="bg-amber-100 text-amber-700">
          {fallbackIcon}
        </AvatarFallback>
      </Avatar>
    );
  }

  if (isLottieUrl && !lottieError) {
    return (
      <Avatar
        className={`${className} overflow-hidden`}
        style={{ width: size, height: size }}
      >
        <Player
          autoplay
          loop
          src={iconUrl}
          style={{ height: size, width: size }}
          onError={() => setLottieError(true)}
        />
      </Avatar>
    );
  }

  // If Lottie failed or it's a regular image
  if (lottieError) {
    return (
      <Avatar className={className} style={{ width: size, height: size }}>
        <AvatarFallback className="bg-amber-100 text-amber-700">
          {fallbackIcon}
        </AvatarFallback>
      </Avatar>
    );
  }

  return (
    <Avatar className={className} style={{ width: size, height: size }}>
      <AvatarImage
        src={iconUrl}
        alt={alt}
        onError={() => setImageError(true)}
      />
      <AvatarFallback className="bg-amber-100 text-amber-700">
        {fallbackIcon}
      </AvatarFallback>
    </Avatar>
  );
};
